import IKey from '@/common/constants/ikey'
import { JS_WEB_ERROR_CODES } from '@/common/constants/jsWebErrorCodes'
import type { JsWebErrorCode } from '@/common/constants/jsWebErrorCodes'
import { JsWebClientError } from '@/common/exceptions'
import { createBaseUniDataResponse } from '@/common/utils/uniDataWsResponse'

export interface JsWebExceptionDetails {
  code: JsWebErrorCode
  description?: string
  developerMessage?: string
}

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') {
    return null
  }
  return value as Record<string, unknown>
}

const asJsWebErrorCode = (value: number | null): JsWebErrorCode | null => {
  if (value == null) {
    return null
  }
  const allowedCodes = Object.values(JS_WEB_ERROR_CODES) as number[]
  return allowedCodes.includes(value) ? (value as JsWebErrorCode) : null
}

export function normalizeJsWebException(error: unknown): JsWebExceptionDetails {
  const errorRecord = asRecord(error)
  const responseRecord = asRecord(errorRecord?.response)
  const responseDataRecord = asRecord(responseRecord?.data)

  if (error instanceof JsWebClientError) {
    return {
      code: error.code,
      description: error.description ?? error.message,
      developerMessage: error.developerMessage,
    }
  }

  const rawDescription =
    errorRecord?.description ??
    errorRecord?.message ??
    responseDataRecord?.description ??
    responseDataRecord?.message

  const rawDeveloperMessage =
    errorRecord?.developerMessage ??
    errorRecord?.['developer-message'] ??
    responseDataRecord?.developerMessage ??
    responseDataRecord?.['developer-message'] ??
    errorRecord?.stack

  const normalizedDescription =
    rawDescription != null ? String(rawDescription) : 'Client websocket request error'
  const normalizedDeveloperMessage =
    rawDeveloperMessage != null ? String(rawDeveloperMessage) : undefined

  const explicitCode =
    asJsWebErrorCode(typeof errorRecord?.code === 'number' ? errorRecord.code : null) ??
    asJsWebErrorCode(typeof responseDataRecord?.code === 'number' ? responseDataRecord.code : null)

  return {
    code: explicitCode ?? JS_WEB_ERROR_CODES.GENERIC_CLIENT_ERROR,
    description: normalizedDescription,
    developerMessage: normalizedDeveloperMessage,
  }
}

function buildExceptionFieldMap(details: JsWebExceptionDetails): Record<string, unknown> {
  const exceptionFields: Record<string, unknown> = {
    code: {
      type: 'Long',
      value: details.code,
    },
  }

  if (details.description) {
    exceptionFields.description = {
      type: 'String',
      value: details.description,
    }
  }

  if (details.developerMessage) {
    exceptionFields['developer-message'] = {
      type: 'String',
      value: details.developerMessage,
    }
  }

  return exceptionFields
}

/**
 * UniData-ответ WS js-request с полем `__js_web_exception` (WUI-312).
 */
export function createJsWebExceptionResponse(error: unknown): Record<string, unknown> {
  const details = normalizeJsWebException(error)
  const innerExceptionUniData = createBaseUniDataResponse(buildExceptionFieldMap(details))

  return createBaseUniDataResponse({
    [IKey.JS_WEB_EXCEPTION]: {
      type: 'UniData',
      value: innerExceptionUniData,
    },
  })
}
